import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Chip, Stack, IconButton, Button, Grid } from '@mui/material';
import { MdChevronLeft, MdChevronRight, MdArrowBack } from 'react-icons/md';
import TopBar from '../components/topbar';
import SidebarNav from '../components/Sidebar';
import Loadingrevolver from '../assets/loading.gif';
import RanchMap from '../components/RanchMap';

const panelStyle = {
    background: 'rgba(255, 255, 255, 0.23)',
    border: '2px solid rgba(120, 53, 15, 0.6)',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
};

const chipStyle = {
    backgroundColor: 'rgba(120, 53, 15, 0.4)',
    color: '#ffffff',
    border: '1px solid rgba(245, 158, 11, 0.5)'
};

function PropertyInfo() {
    const { id } = useParams();
    const [listing, setListing] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeImage, setActiveImage] = useState(0);

    useEffect(() => {
        const fetchListing = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(`${import.meta.env.VITE_REACT_API_URL}/api/listings/properties/${id}`);
                if (!response.ok) {
                    throw new Error("Listing not found");
                }
                const result = await response.json();
                setListing(result);
            } catch (err) {
                setError(err.message);
                console.error("Error fetching listing:", err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchListing();
    }, [id]);

    const parseJsonArray = (raw) => {
        try {
            return JSON.parse(raw) || [];
        } catch {
            return [];
        }
    };

    return (
        <div className="content">
            <div className='topNav'>
                <TopBar />
            </div>
            <div className='sidebar-container'>
                <SidebarNav />
            </div>

            <Box sx={{ pt: 4, maxWidth: 1000, mx: 'auto' }}>
                <Button
                    component={Link}
                    to="/pages/properties"
                    startIcon={<MdArrowBack />}
                    sx={{ color: 'rgba(254, 243, 199, 0.8)', mb: 3, display: 'flex', width: 'fit-content' }}
                >
                    Back to Properties
                </Button>

                {isLoading ? (
                    <Box sx={{ textAlign: 'center' }}>
                        <img src={Loadingrevolver} alt="loading..." style={{ width: '10%', height: '10%' }} />
                    </Box>
                ) : error ? (
                    <Typography sx={{ color: '#f87171', textAlign: 'center' }}>Error: {error}</Typography>
                ) : (
                    <Box>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={7}>
                                {(() => {
                                    const images = parseJsonArray(listing.images);
                                    return images.length > 0 ? (
                                        <>
                                            <Box sx={{ position: 'relative' }}>
                                                <img src={images[activeImage]} alt={listing.title} style={{ width: '100%', height: '450px', objectFit: 'cover', borderRadius: '8px' }} />
                                                {images.length > 1 && (
                                                    <>
                                                        <IconButton onClick={() => setActiveImage((activeImage - 1 + images.length) % images.length)} sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.7)' }}>
                                                            <MdChevronLeft />
                                                        </IconButton>
                                                        <IconButton onClick={() => setActiveImage((activeImage + 1) % images.length)} sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.7)' }}>
                                                            <MdChevronRight />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Box>
                                            {images.length > 1 && (
                                                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                                                    {images.map((img, index) => (
                                                        <img
                                                            key={index}
                                                            src={img}
                                                            alt={`thumbnail ${index}`}
                                                            onClick={() => setActiveImage(index)}
                                                            style={{ width: '90px', height: '65px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer', border: activeImage === index ? '2px solid #f59e0b' : '2px solid transparent' }}
                                                        />
                                                    ))}
                                                </Stack>
                                            )}
                                        </>
                                    ) : (
                                        <Box sx={{ width: '100%', height: '450px', borderRadius: '8px', ...panelStyle }} />
                                    );
                                })()}
                            </Grid>

                            <Grid item xs={12} md={5}>
                                <Typography variant="overline" sx={{ color: '#f59e0b', letterSpacing: 2 }}>Property Listing</Typography>
                                <Typography variant="h3" sx={{ color: '#ffffff', fontFamily: 'Playfair Display, serif', mb: 2 }}>{listing.title}</Typography>

                                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                                    {parseJsonArray(listing.tags).map((tag, index) => (
                                        <Chip key={index} label={tag} size="small" sx={chipStyle} />
                                    ))}
                                </Stack>
                                <Typography sx={{ color: 'rgba(254, 243, 199, 0.8)', mb: 2 }}>
                                    {listing.location}{listing.region ? ` — ${listing.region}` : ''}
                                    {listing.property_number ? ` · Property ${listing.property_number}` : ''}
                                </Typography>
                                {listing.description && (
                                    <Typography sx={{ color: '#ffffff', mb: 3 }}>{listing.description}</Typography>
                                )}

                                <Stack spacing={2}>
                                    <Card sx={panelStyle}>
                                        <CardContent>
                                            <Typography variant="subtitle2" sx={{ color: 'rgba(254, 243, 199, 0.7)' }}>Current Bid</Typography>
                                            <Typography variant="h5" sx={{ color: '#ffffff' }}>${listing.current_bid}</Typography>
                                        </CardContent>
                                    </Card>
                                    {listing.contact_info && (
                                        <Card sx={panelStyle}>
                                            <CardContent>
                                                <Typography variant="subtitle2" sx={{ color: 'rgba(254, 243, 199, 0.7)' }}>Contact Information</Typography>
                                                <Typography sx={{ color: '#ffffff' }}>{listing.contact_info}</Typography>
                                            </CardContent>
                                        </Card>
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>

                        {(listing.map_image_url || (listing.map_x !== null && listing.map_y !== null)) && (
                            <Box sx={{ width: '100%', mt: 4, mb: 4 }}>
                                {listing.map_image_url ? (
                                    <img src={listing.map_image_url} alt="Map" style={{ width: '100%', borderRadius: '8px' }} />
                                ) : (
                                    <RanchMap position={[Number(listing.map_x), Number(listing.map_y)]} height={400} />
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </div>
    );
};

export default PropertyInfo;
